import { useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    HolidayFormSchema,
    holidayFormDefaults,
    HOLIDAY_SCOPES,
} from '../../models/holiday.schemas';
import type { HolidayFormData, HolidayFormValues } from '../../models/holiday.schemas';
import { isoToBrDate } from '../../utils/vacationDates';
import { IsoDatePicker } from '../VacationRequestForm/components/IsoDatePicker';
import {
    CANCEL_LABEL,
    CREATE_TITLE,
    EDIT_TITLE,
    FIELD_LABELS,
    NAME_PLACEHOLDER,
    RECURRING_HELP,
    SUBMITTING_LABEL,
    SUBMIT_CREATE,
    SUBMIT_EDIT,
} from './holidayFormModal.definitions';
import type { HolidayFormModalProps } from './holidayFormModal.types';

export function HolidayFormModal({
    isOpen,
    holiday,
    onClose,
    onSubmit,
    isSubmitting = false,
}: HolidayFormModalProps) {
    const isEditing = Boolean(holiday);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<HolidayFormValues, unknown, HolidayFormData>({
        resolver: zodResolver(HolidayFormSchema),
        defaultValues: holidayFormDefaults,
    });

    useEffect(() => {
        if (!isOpen) return;

        reset(
            holiday
                ? {
                      date: isoToBrDate(holiday.date),
                      name: holiday.name,
                      scope: holiday.scope,
                      recurring: holiday.recurring,
                  }
                : holidayFormDefaults
        );
    }, [isOpen, holiday, reset]);

    if (!isOpen) return null;

    const submit = handleSubmit((data) =>
        onSubmit(
            {
                date: data.date,
                name: data.name,
                scope: data.scope,
                recurring: data.recurring,
            },
            holiday?.id
        )
    );

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    <form onSubmit={submit}>
                        <div className="space-y-4 p-6">
                            <Dialog.Title className="text-base font-semibold text-raro-gray-900 font-montserrat">
                                {isEditing ? EDIT_TITLE : CREATE_TITLE}
                            </Dialog.Title>

                            <div>
                                <label
                                    htmlFor="holiday-date"
                                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                                >
                                    {FIELD_LABELS.date}
                                </label>
                                <div className="mt-1">
                                    <Controller
                                        control={control}
                                        name="date"
                                        render={({ field }) => (
                                            <IsoDatePicker
                                                id="holiday-date"
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                            />
                                        )}
                                    />
                                </div>
                                {errors.date && (
                                    <p className="mt-1 text-xs text-red-600 font-montserrat">
                                        {errors.date.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="holiday-name"
                                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                                >
                                    {FIELD_LABELS.name}
                                </label>
                                <input
                                    id="holiday-name"
                                    type="text"
                                    placeholder={NAME_PLACEHOLDER}
                                    aria-invalid={Boolean(errors.name)}
                                    {...register('name')}
                                    className="mt-1 block w-full rounded-xl border border-primary-200 p-2.5 text-sm text-raro-gray-900 focus:border-primary-500 focus:ring-primary-500 font-montserrat"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-xs text-red-600 font-montserrat">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="holiday-scope"
                                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                                >
                                    {FIELD_LABELS.scope}
                                </label>
                                <select
                                    id="holiday-scope"
                                    {...register('scope')}
                                    className="mt-1 block w-full rounded-xl border border-primary-200 p-2.5 text-sm text-raro-gray-900 focus:border-primary-500 focus:ring-primary-500 font-montserrat"
                                >
                                    {HOLIDAY_SCOPES.map((scope) => (
                                        <option key={scope} value={scope}>
                                            {scope}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="rounded-xl border border-dashed border-primary-200 p-3">
                                <label className="flex gap-2">
                                    <input
                                        type="checkbox"
                                        {...register('recurring')}
                                        className="mt-0.5 h-4 w-4 shrink-0 rounded text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium text-raro-gray-900 font-montserrat">
                                            {FIELD_LABELS.recurring}
                                        </span>
                                        <span className="mt-0.5 block text-xs text-raro-gray-600 font-montserrat">
                                            {RECURRING_HELP}
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-raro-gray-700 transition-colors hover:bg-gray-50 font-montserrat"
                            >
                                {CANCEL_LABEL}
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60 font-montserrat"
                            >
                                {isSubmitting
                                    ? SUBMITTING_LABEL
                                    : isEditing
                                      ? SUBMIT_EDIT
                                      : SUBMIT_CREATE}
                            </button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

export default HolidayFormModal;
